import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { BarChart3, Copy, Download, Link2, Plus, RefreshCw, X } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select } from '@/components/ui/Field';
import { Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';

// Website traffic: where visitors come from (Facebook, Instagram, Google,
// tracked /go/ links…), what they look at and which orders they place.
// Numbers come from the DB function traffic_analytics(); tracked links live
// in traffic_links. The same file is in erp-thekseoul, rxk and
// thekseoul-admin-hub (src/components/analytics/) — keep them identical.

export const ALL_SITES = ['rxk', 'pick-k', 'medi-k', 'thekseoul'] as const;
const SITE_LABEL: Record<string, string> = { rxk: 'Rx.K Seoul', 'pick-k': 'Pick.K Seoul', 'medi-k': 'Medi.K Seoul', thekseoul: 'The.K Seoul' };
const SITE_URL: Record<string, string> = {
  rxk: 'https://rxk.thekseoul.com',
  'pick-k': 'https://pick.thekseoul.com',
  'medi-k': 'https://medik.thekseoul.com',
  thekseoul: 'https://thekseoul.com',
};

// Each source keeps its colour everywhere (chart, tables, legend).
const SOURCE_COLOR: Record<string, string> = {
  Facebook: '#2F6FDF',
  Instagram: '#D6417A',
  'Facebook / Instagram': '#3E4FA8',
  Google: '#E09B26',
  WhatsApp: '#2E9E5B',
  TikTok: '#1F9EA8',
  YouTube: '#C9442E',
  'QR code': '#7B5CC4',
  Email: '#A9773A',
  SMS: '#5F8F3A',
  'Our other sites': '#5B7083',
  'Other search': '#9C8A4E',
  'Other sites': '#9AA3B2',
  Direct: '#C3C9D4',
  'Not tracked': '#E2E5EA',
  'Phone call': '#4E7D96',
  'Walk-in': '#8C7A5B',
  Referral: '#6F8F5E',
};
const colorOf = (s: string) => SOURCE_COLOR[s] ?? '#9AA3B2';

const LINK_SOURCES = [
  ['social', 'Facebook + Instagram (one link for both)'],
  ['facebook', 'Facebook'],
  ['instagram', 'Instagram'],
  ['whatsapp', 'WhatsApp'],
  ['tiktok', 'TikTok'],
  ['youtube', 'YouTube'],
  ['google', 'Google'],
  ['email', 'Email'],
  ['sms', 'SMS'],
  ['qr', 'QR code'],
  ['other', 'Other'],
] as const;
const LINK_MEDIUMS = ['post', 'story', 'reel', 'bio', 'ad', 'message', 'group', 'print', 'other'];

type Range = '1' | '7' | '30' | '90' | 'custom';

interface Data {
  kpis: { visitors: number; views: number; orders: number; revenue: number; tracked_orders: number; prev_visitors: number; prev_views: number; prev_orders: number; prev_revenue: number };
  daily: { day: string; source: string; visitors: number }[];
  sources: { source: string; visitors: number; views: number; orders: number; revenue: number }[];
  campaigns: { campaign: string; name: string | null; link_site: string | null; source: string | null; visitors: number; orders: number; revenue: number }[];
  pages: { site_key: string; path: string; views: number; visitors: number }[];
  products: { site_key: string; product: string; views: number; visitors: number; top_source: string | null }[];
  landing: { site_key: string; path: string; visitors: number; top_source: string | null }[];
  devices: { device: string; visitors: number }[];
  sites: { site: string; visitors: number }[];
  recent: { at: string; site_key: string; path: string; source: string; campaign: string | null; device: string | null }[];
  key_pages?: { page: string; source: string; visitors: number; views: number }[];
  staff_orders?: { came_from: string; source: string; orders: number; revenue: number }[];
}

interface TrafficLink {
  id: string;
  site_key: string;
  code: string;
  name: string;
  target_path: string;
  utm_source: string;
  utm_medium: string | null;
  utm_campaign: string | null;
  note: string | null;
  is_active: boolean;
  created_at: string;
}

const num = (n: number) => Math.round(n || 0).toLocaleString('en-IN');
const bdt = (n: number) => `৳${num(n)}`;
const pct = (a: number, b: number) => (b ? `${((a / b) * 100).toFixed(1)}%` : '—');
const dhakaDay = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Dhaka' });
const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

function Delta({ now, before }: { now: number; before: number }) {
  if (!before && !now) return <span className="text-[11px] text-ink-faint">—</span>;
  if (!before) return <span className="text-[11px] font-semibold text-emerald-600">new</span>;
  const d = ((now - before) / before) * 100;
  const up = d >= 0;
  return (
    <span className={`text-[11px] font-semibold ${up ? 'text-emerald-600' : 'text-rose-600'}`}>
      {up ? '▲' : '▼'} {Math.abs(d).toFixed(0)}% <span className="font-normal text-ink-faint">vs previous</span>
    </span>
  );
}

function Kpi({ label, value, delta, hint }: { label: string; value: string; delta?: ReactNode; hint?: string }) {
  return (
    <div className="rounded-xl border border-border-subtle bg-white px-4 py-3">
      <p className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-faint">{label}</p>
      <p className="mt-1 text-[22px] font-bold text-ink" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </p>
      <div className="mt-0.5 min-h-[16px]">{delta}</div>
      {hint && <p className="text-[10.5px] text-ink-faint">{hint}</p>}
    </div>
  );
}

function Swatch({ source }: { source: string }) {
  return <span className="inline-block h-2.5 w-2.5 flex-shrink-0 rounded-sm" style={{ background: colorOf(source) }} />;
}

// Stacked daily bars by source, with a hover tooltip per day.
function DailyChart({ data, from, to }: { data: Data['daily']; from: Date; to: Date }) {
  const [hover, setHover] = useState<number | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const days = useMemo(() => {
    const out: string[] = [];
    const d = new Date(from);
    while (d < to && out.length < 400) {
      out.push(dhakaDay(d));
      d.setDate(d.getDate() + 1);
    }
    return [...new Set(out)];
  }, [from, to]);
  const totals = new Map<string, number>();
  data.forEach((r) => totals.set(r.source, (totals.get(r.source) ?? 0) + r.visitors));
  const order = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([s]) => s);
  const top = order.slice(0, 7);
  const group = (s: string) => (top.includes(s) ? s : 'Other sites');
  const series = [...new Set(order.map(group))];
  const byDay = new Map<string, Record<string, number>>();
  data.forEach((r) => {
    const m = byDay.get(r.day) ?? {};
    const g = group(r.source);
    m[g] = (m[g] ?? 0) + r.visitors;
    byDay.set(r.day, m);
  });
  const dayTotal = (d: string) => Object.values(byDay.get(d) ?? {}).reduce((a, b) => a + b, 0);
  const max = Math.max(1, ...days.map(dayTotal));
  const W = 900;
  const H = 220;
  const P = { t: 10, r: 8, b: 24, l: 34 };
  const bw = (W - P.l - P.r) / Math.max(1, days.length);
  const y = (v: number) => P.t + (H - P.t - P.b) * (1 - v / max);
  const ticks = [0, Math.round(max / 2), max];

  return (
    <div ref={wrap} className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[220px]" onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={P.l} x2={W - P.r} y1={y(t)} y2={y(t)} stroke="#E7E9EE" strokeWidth={1} />
            <text x={P.l - 6} y={y(t) + 3} textAnchor="end" fontSize={10} fill="#8A94A6">
              {t}
            </text>
          </g>
        ))}
        {days.map((d, i) => {
          let acc = 0;
          const m = byDay.get(d) ?? {};
          return (
            <g key={d} onMouseEnter={() => setHover(i)}>
              <rect x={P.l + i * bw} y={P.t} width={bw} height={H - P.t - P.b} fill={hover === i ? '#F3F5F9' : 'transparent'} />
              {series.map((s) => {
                const v = m[s] ?? 0;
                if (!v) return null;
                const y1 = y(acc + v);
                const h = y(acc) - y1;
                acc += v;
                return <rect key={s} x={P.l + i * bw + bw * 0.15} y={y1 + 1} width={Math.max(1, bw * 0.7)} height={Math.max(0, h - 1)} rx={1.5} fill={colorOf(s)} />;
              })}
              {(days.length <= 14 || i % Math.ceil(days.length / 10) === 0) && (
                <text x={P.l + i * bw + bw / 2} y={H - 8} textAnchor="middle" fontSize={10} fill="#8A94A6">
                  {new Date(`${d}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hover !== null && days[hover] && (
        <div className="pointer-events-none absolute top-2 z-10 min-w-[160px] rounded-lg border border-border-subtle bg-white px-3 py-2 text-[11.5px] shadow-lg" style={{ left: `min(calc(${((hover + 0.5) / days.length) * 100}% + 8px), calc(100% - 170px))` }}>
          <p className="mb-1 font-semibold text-ink">
            {new Date(`${days[hover]}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} · {dayTotal(days[hover])} visitors
          </p>
          {series
            .filter((s) => (byDay.get(days[hover])?.[s] ?? 0) > 0)
            .map((s) => (
              <div key={s} className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-1.5 text-ink-faint">
                  <Swatch source={s} /> {s}
                </span>
                <span className="font-semibold text-ink">{byDay.get(days[hover])?.[s]}</span>
              </div>
            ))}
        </div>
      )}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {series.map((s) => (
          <span key={s} className="flex items-center gap-1.5 text-[11.5px] text-ink">
            <Swatch source={s} /> {s}
          </span>
        ))}
      </div>
    </div>
  );
}

function Table({ head, rows, empty }: { head: ReactNode[]; rows: ReactNode[][]; empty: string }) {
  if (rows.length === 0) return <p className="py-6 text-center text-[12.5px] text-ink-faint">{empty}</p>;
  return (
    <div className="-mx-5 overflow-x-auto">
      <table className="w-full text-left text-[12.5px]">
        <thead>
          <tr className="border-b border-border-subtle text-[10.5px] uppercase tracking-wider text-ink-faint">
            {head.map((h, i) => (
              <th key={i} className={`px-5 py-2 font-semibold ${i > 0 ? 'text-right' : ''}`}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-surface-muted/60">
              {r.map((c, j) => (
                <td key={j} className={`px-5 py-2 ${j > 0 ? 'text-right whitespace-nowrap' : ''}`} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ShareBar({ value, max, color }: { value: number; max: number; color: string }) {
  return (
    <div className="mt-1 h-1.5 w-full rounded-full bg-surface-muted">
      <div className="h-1.5 rounded-full" style={{ width: `${max ? (value / max) * 100 : 0}%`, background: color }} />
    </div>
  );
}

function downloadCsv(name: string, head: string[], rows: (string | number)[][]) {
  const esc = (v: string | number) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [head, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}

// Rx.K Seoul product pages + Where to buy, one row each, visitors per source.
const KEY_SOURCES = ['Facebook', 'Instagram', 'Google', 'QR code', 'Direct'];
const SKU_ORDER = ['BST', 'SRM', 'CRM', 'SET'];

function KeyPagesCard({ rows, products }: { rows: NonNullable<Data['key_pages']>; products: { slug: string; sku: string; name: string }[] }) {
  const pages = [
    ...[...products]
      .sort((a, b) => {
        const i = (s: string) => {
          const n = SKU_ORDER.findIndex((k) => s.toUpperCase().includes(k));
          return n < 0 ? 99 : n;
        };
        return i(a.sku) - i(b.sku);
      })
      .map((p) => ({ key: p.slug, label: p.name, path: `/products/${p.slug}` })),
    { key: 'stores', label: 'Where to buy', path: '/stores' },
  ];
  const cell = (page: string, source: string) => rows.filter((r) => r.page === page && r.source === source).reduce((a, r) => a + r.visitors, 0);
  const total = (page: string) => rows.filter((r) => r.page === page).reduce((a, r) => a + r.visitors, 0);
  const other = (page: string) => rows.filter((r) => r.page === page && !KEY_SOURCES.includes(r.source)).reduce((a, r) => a + r.visitors, 0);

  return (
    <Card>
      <CardHeader title="Rx.K Seoul product pages & Where to buy" subtitle="How many visitors reached each page, by where they came from" />
      <CardBody>
        <div className="-mx-5 overflow-x-auto">
          <table className="w-full text-left text-[12.5px]">
            <thead>
              <tr className="border-b border-border-subtle text-[10.5px] uppercase tracking-wider text-ink-faint">
                <th className="px-5 py-2 font-semibold">Page</th>
                <th className="px-3 py-2 text-right font-semibold">Visitors</th>
                {[...KEY_SOURCES, 'Other'].map((s) => (
                  <th key={s} className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      <Swatch source={s === 'Other' ? 'Other sites' : s} /> {s}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {pages.map((p) => {
                const t = total(p.key);
                const parts = [...KEY_SOURCES.map((s) => [s, cell(p.key, s)] as const), ['Other sites', other(p.key)] as const];
                return (
                  <tr key={p.key} className="hover:bg-surface-muted/60" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    <td className="px-5 py-2.5">
                      <p className="font-semibold text-ink">{p.label}</p>
                      <p className="font-mono text-[10.5px] text-ink-faint">{p.path}</p>
                      <div className="mt-1 flex h-1.5 w-full max-w-[220px] gap-px overflow-hidden rounded-full bg-surface-muted">
                        {t > 0 && parts.map(([s, v]) => (v > 0 ? <div key={s} style={{ width: `${(v / t) * 100}%`, background: colorOf(s) }} /> : null))}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-bold text-ink">{num(t)}</td>
                    {parts.map(([s, v]) => (
                      <td key={s} className={`px-3 py-2.5 text-right ${v ? 'text-ink' : 'text-ink-faint/50'}`}>
                        {v ? num(v) : '–'}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[11px] text-ink-faint">
          A visitor is counted under the source they arrived from, even if they opened this page later in the same visit. Korean and Bengali versions of a page are included.
        </p>
      </CardBody>
    </Card>
  );
}

// ── Link manager ────────────────────────────────────────────────────────
function LinkModal({ sites, link, onClose, onSaved }: { sites: string[]; link: TrafficLink | null; onClose: () => void; onSaved: () => void }) {
  const { push } = useToast();
  const [form, setForm] = useState({
    site_key: link?.site_key ?? sites[0],
    name: link?.name ?? '',
    code: link?.code ?? '',
    utm_source: link?.utm_source ?? 'social',
    utm_medium: link?.utm_medium ?? 'post',
    utm_campaign: link?.utm_campaign ?? '',
    target_path: link?.target_path ?? '/',
    note: link?.note ?? '',
  });
  const [codeTouched, setCodeTouched] = useState(!!link);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v, ...(k === 'name' && !codeTouched ? { code: slugify(v) } : {}) }));
  const url = `${SITE_URL[form.site_key]}/go/${form.code || 'your-link'}`;

  const save = async () => {
    const code = slugify(form.code);
    if (!form.name.trim() || !code) return push('error', 'Give the link a name and a short code');
    if (!form.target_path.startsWith('/')) return push('error', 'Page must start with / (e.g. /products/daily-rescue-serum)');
    setBusy(true);
    const row = {
      site_key: form.site_key,
      name: form.name.trim(),
      code,
      utm_source: form.utm_source,
      utm_medium: form.utm_medium || null,
      utm_campaign: form.utm_campaign.trim() || null,
      target_path: form.target_path.trim(),
      note: form.note.trim() || null,
    };
    const { error } = link ? await supabase.from('traffic_links').update(row).eq('id', link.id) : await supabase.from('traffic_links').insert(row);
    setBusy(false);
    if (error) return push('error', error.code === '23505' ? 'That code is already used on this site' : error.message);
    push('success', link ? 'Link updated' : 'Link created — copy it from the list');
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
          <p className="text-[14px] font-bold text-ink">{link ? 'Edit tracked link' : 'New tracked link'}</p>
          <button onClick={onClose} disabled={busy} className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-faint hover:bg-surface-muted cursor-pointer" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 px-5 py-4">
          {sites.length > 1 && (
            <Field label="Site">
              <Select value={form.site_key} onChange={(e) => set('site_key', e.target.value)} disabled={!!link}>
                {sites.map((s) => (
                  <option key={s} value={s}>
                    {SITE_LABEL[s]}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label="Name (for you)">
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="FB post – Serum offer, October" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Where you'll share it">
              <Select value={form.utm_source} onChange={(e) => set('utm_source', e.target.value)}>
                {LINK_SOURCES.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="As a">
              <Select value={form.utm_medium} onChange={(e) => set('utm_medium', e.target.value)}>
                {LINK_MEDIUMS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label="Opens page">
            <Input value={form.target_path} onChange={(e) => set('target_path', e.target.value)} placeholder="/ or /products/daily-rescue-serum" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Short code">
              <Input
                value={form.code}
                disabled={!!link}
                onChange={(e) => {
                  setCodeTouched(true);
                  set('code', e.target.value.toLowerCase());
                }}
                placeholder="serum-oct"
              />
            </Field>
            <Field label="Campaign (optional)">
              <Input value={form.utm_campaign} onChange={(e) => set('utm_campaign', e.target.value)} placeholder="Same as code if empty" />
            </Field>
          </div>
          <Field label="Note (optional)">
            <Input value={form.note} onChange={(e) => set('note', e.target.value)} />
          </Field>
          <div className="rounded-lg bg-surface-muted px-3 py-2 text-[12px]">
            <span className="text-ink-faint">Link: </span>
            <span className="break-all font-mono text-ink">{url}</span>
          </div>
        </div>
        <div className="flex gap-2 border-t border-border-subtle px-5 py-4">
          <Button variant="secondary" onClick={onClose} disabled={busy} className="flex-1">
            Cancel
          </Button>
          <Button onClick={save} loading={busy} className="flex-1">
            {link ? 'Save' : 'Create link'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function LinksPanel({ sites, stats, canManage, onChanged }: { sites: string[]; stats: Data['campaigns']; canManage: boolean; onChanged: () => void }) {
  const { push } = useToast();
  const [links, setLinks] = useState<TrafficLink[] | null>(null);
  const [editing, setEditing] = useState<TrafficLink | 'new' | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase.from('traffic_links').select('*').in('site_key', sites).order('created_at', { ascending: false });
    if (error) push('error', error.message);
    setLinks((data as TrafficLink[]) ?? []);
  }, [sites, push]);
  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (l: TrafficLink) => {
    const { error } = await supabase.from('traffic_links').update({ is_active: !l.is_active }).eq('id', l.id);
    if (error) return push('error', error.message);
    load();
  };
  const copy = async (l: TrafficLink) => {
    try {
      await navigator.clipboard.writeText(`${SITE_URL[l.site_key]}/go/${l.code}`);
      push('success', 'Link copied');
    } catch {
      push('error', 'Could not copy — select the link and copy it');
    }
  };
  const statOf = (l: TrafficLink) => stats.find((s) => s.campaign === l.code);

  return (
    <Card>
      <CardHeader
        title="Tracked links"
        subtitle={
          canManage
            ? 'Short links for posts, bios, ads and QR codes — each one counts its own visitors and orders'
            : 'Each link counts its own visitors and orders. Links are created and edited in ERP → Website Analytics.'
        }
        icon={<Link2 className="h-4 w-4" />}
        action={
          canManage ? (
            <Button size="sm" onClick={() => setEditing('new')}>
              <Plus className="h-3.5 w-3.5" /> New link
            </Button>
          ) : undefined
        }
      />
      <CardBody>
        {!links ? (
          <Spinner />
        ) : links.length === 0 ? (
          <p className="py-6 text-center text-[12.5px] text-ink-faint">{canManage ? 'No tracked links yet. Create one, then share it instead of the plain site address.' : 'No tracked links yet. Create them in ERP → Website Analytics.'}</p>
        ) : (
          <Table
            head={canManage ? ['Link', 'Shared on', 'Visitors', 'Orders', 'Sales', ''] : ['Link', 'Shared on', 'Visitors', 'Orders', 'Sales']}
            empty=""
            rows={links.map((l) => {
              const s = statOf(l);
              return [
                <div key="n" className={l.is_active ? '' : 'opacity-50'}>
                  <p className="font-semibold text-ink">{l.name}</p>
                  <button onClick={() => copy(l)} className="flex items-center gap-1 font-mono text-[11px] text-primary-700 hover:underline cursor-pointer">
                    {SITE_URL[l.site_key].replace('https://', '')}/go/{l.code} <Copy className="h-3 w-3" />
                  </button>
                  <p className="text-[10.5px] text-ink-faint">opens {l.target_path}</p>
                </div>,
                <span key="s" className="text-ink-faint">
                  {l.utm_source === 'social' ? 'Facebook + Instagram' : l.utm_source}
                  {l.utm_medium ? ` · ${l.utm_medium}` : ''}
                </span>,
                num(s?.visitors ?? 0),
                num(s?.orders ?? 0),
                bdt(s?.revenue ?? 0),
                ...(canManage ? [<div key="a" className="flex justify-end gap-2">
                  <button onClick={() => setEditing(l)} className="text-[11.5px] font-semibold text-ink-faint hover:text-ink cursor-pointer">
                    Edit
                  </button>
                  <button onClick={() => toggle(l)} className={`text-[11.5px] font-semibold cursor-pointer ${l.is_active ? 'text-rose-600' : 'text-emerald-600'}`}>
                    {l.is_active ? 'Turn off' : 'Turn on'}
                  </button>
                </div>] : []),
              ];
            })}
          />
        )}
      </CardBody>
      {canManage && editing && (
        <LinkModal
          sites={sites}
          link={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
            onChanged();
          }}
        />
      )}
    </Card>
  );
}

// ── Page ────────────────────────────────────────────────────────────────
// manageLinks: only ERP creates / edits tracked links; rxk and Admin Hub only view them.
export function AnalyticsDashboard({ sites: sitesProp, manageLinks = false }: { sites?: string[]; manageLinks?: boolean }) {
  // Callers pass a fresh array on every render; key it by value so the data
  // loaders below don't re-run (and re-fetch) on each render.
  const allowedKey = (sitesProp ?? ALL_SITES).join(',');
  const allowed = useMemo(() => allowedKey.split(','), [allowedKey]);
  const { push } = useToast();
  const [range, setRange] = useState<Range>('30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [site, setSite] = useState<string>('all');
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [rxkProducts, setRxkProducts] = useState<{ slug: string; sku: string; name: string }[]>([]);

  const sites = useMemo(() => (site === 'all' ? allowed : [site]), [site, allowed]);
  const { from, to } = useMemo(() => {
    if (range === 'custom' && customFrom && customTo) {
      return { from: new Date(`${customFrom}T00:00:00+06:00`), to: new Date(`${customTo}T23:59:59+06:00`) };
    }
    const end = new Date();
    const start = new Date(`${dhakaDay(end)}T00:00:00+06:00`);
    start.setDate(start.getDate() - (Number(range === 'custom' ? 30 : range) - 1));
    return { from: start, to: end };
  }, [range, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: d, error } = await supabase.rpc('traffic_analytics', { p_sites: sites, p_from: from.toISOString(), p_to: to.toISOString() });
    if (error) push('error', error.message);
    setData((d as Data) ?? null);
    setLoading(false);
  }, [sites, from, to, push]);
  useEffect(() => {
    load();
  }, [load]);

  // Product names for the product table (rxk uses the page slug, Pick.K the product id or slug).
  useEffect(() => {
    Promise.all([
      supabase.from('rxk_product_content').select('slug, sku'),
      supabase.from('core_products').select('sku, name_en'),
      supabase.from('pickk_products').select('id, slug, name_en'),
    ]).then(([rc, cp, pp]) => {
      const sku = new Map(((cp.data as { sku: string; name_en: string }[]) ?? []).map((r) => [r.sku, r.name_en]));
      const m = new Map<string, string>();
      const rxk = ((rc.data as { slug: string; sku: string }[]) ?? []).map((r) => ({ slug: r.slug, sku: r.sku, name: sku.get(r.sku) ?? r.slug }));
      rxk.forEach((r) => m.set(r.slug, r.name));
      setRxkProducts(rxk);
      ((pp.data as { id: string; slug: string | null; name_en: string }[]) ?? []).forEach((r) => {
        m.set(r.id, r.name_en);
        if (r.slug) m.set(r.slug, r.name_en);
      });
      setNames(m);
    });
  }, []);

  const k = data?.kpis;
  const maxSource = Math.max(1, ...(data?.sources ?? []).map((s) => s.visitors));
  const siteTag = (s: string) => (allowed.length > 1 ? <span className="mr-1.5 rounded bg-surface-muted px-1.5 py-0.5 text-[10px] text-ink-faint">{SITE_LABEL[s] ?? s}</span> : null);

  const exportCsv = () => {
    if (!data) return;
    downloadCsv(
      `analytics_${dhakaDay(from)}_${dhakaDay(to)}.csv`,
      ['Section', 'Name', 'Visitors', 'Views', 'Orders', 'Sales (BDT)'],
      [
        ...data.sources.map((s) => ['Source', s.source, s.visitors, s.views, s.orders, s.revenue]),
        ...data.campaigns.map((c) => ['Link / campaign', c.name ?? c.campaign, c.visitors, '', c.orders, c.revenue]),
        ...data.pages.map((p) => ['Page', `${SITE_LABEL[p.site_key] ?? p.site_key} ${p.path}`, p.visitors, p.views, '', '']),
        ...data.products.map((p) => ['Product', names.get(p.product) ?? p.product, p.visitors, p.views, '', '']),
      ],
    );
  };

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center gap-1 rounded-xl bg-surface-muted p-1">
            {(
              [
                ['1', 'Today'],
                ['7', '7 days'],
                ['30', '30 days'],
                ['90', '90 days'],
                ['custom', 'Custom'],
              ] as const
            ).map(([v, l]) => (
              <button
                key={v}
                onClick={() => setRange(v)}
                className={`rounded-lg px-3 py-1.5 text-[12.5px] font-semibold cursor-pointer ${range === v ? 'bg-white text-primary-800 shadow-sm' : 'text-ink-faint hover:text-ink'}`}
              >
                {l}
              </button>
            ))}
          </div>
          {range === 'custom' && (
            <div className="flex items-center gap-1.5">
              <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="!w-36" />
              <span className="text-ink-faint">–</span>
              <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="!w-36" />
            </div>
          )}
          {allowed.length > 1 && (
            <Select value={site} onChange={(e) => setSite(e.target.value)} className="!w-44">
              <option value="all">All sites</option>
              {allowed.map((s) => (
                <option key={s} value={s}>
                  {SITE_LABEL[s]}
                </option>
              ))}
            </Select>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={load}>
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
          <Button variant="secondary" size="sm" onClick={exportCsv} disabled={!data}>
            <Download className="h-3.5 w-3.5" /> Excel (CSV)
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <Spinner />
      ) : !data || !k ? (
        <p className="py-10 text-center text-[13px] text-ink-faint">Could not load analytics.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Kpi label="Visitors" value={num(k.visitors)} delta={<Delta now={k.visitors} before={k.prev_visitors} />} />
            <Kpi label="Page views" value={num(k.views)} delta={<Delta now={k.views} before={k.prev_views} />} hint={k.visitors ? `${(k.views / k.visitors).toFixed(1)} per visitor` : undefined} />
            <Kpi label="Web orders" value={num(k.orders)} delta={<Delta now={k.orders} before={k.prev_orders} />} hint={`${num(k.tracked_orders)} with a known source`} />
            <Kpi label="Web sales" value={bdt(k.revenue)} delta={<Delta now={k.revenue} before={k.prev_revenue} />} />
            <Kpi label="Visitors who ordered" value={pct(k.orders, k.visitors)} hint="Orders ÷ visitors" />
          </div>

          <Card>
            <CardHeader title="Visitors by day" subtitle="Coloured by where each visitor came from" icon={<BarChart3 className="h-4 w-4" />} />
            <CardBody>
              <DailyChart data={data.daily} from={from} to={to} />
            </CardBody>
          </Card>

          <div className="grid gap-5 lg:grid-cols-5">
            <div className="lg:col-span-3">
              <Card>
                <CardHeader title="Where visitors come from" subtitle="Each visitor counted once, by how they first arrived" />
                <CardBody>
                  <Table
                    head={['Source', 'Visitors', 'Views', 'Orders', 'Sales', 'Ordered']}
                    empty="No visits in this period."
                    rows={data.sources.map((s) => [
                      <div key="s">
                        <span className="flex items-center gap-2 font-semibold text-ink">
                          <Swatch source={s.source} /> {s.source}
                        </span>
                        <ShareBar value={s.visitors} max={maxSource} color={colorOf(s.source)} />
                      </div>,
                      num(s.visitors),
                      num(s.views),
                      num(s.orders),
                      bdt(s.revenue),
                      pct(s.orders, s.visitors),
                    ])}
                  />
                </CardBody>
              </Card>
            </div>
            <div className="space-y-5 lg:col-span-2">
              <Card>
                <CardHeader title="Devices" />
                <CardBody>
                  {data.devices.length === 0 ? (
                    <p className="text-[12.5px] text-ink-faint">—</p>
                  ) : (
                    data.devices
                      .sort((a, b) => b.visitors - a.visitors)
                      .map((d) => (
                        <div key={d.device} className="mb-2">
                          <div className="flex justify-between text-[12.5px]">
                            <span className="text-ink">{d.device === 'Unknown' ? 'Not recorded (older visits)' : d.device}</span>
                            <span className="font-semibold text-ink">{pct(d.visitors, k.visitors)}</span>
                          </div>
                          <ShareBar value={d.visitors} max={k.visitors} color="#2F6FDF" />
                        </div>
                      ))
                  )}
                </CardBody>
              </Card>
              {allowed.length > 1 && site === 'all' && (
                <Card>
                  <CardHeader title="Sites" />
                  <CardBody>
                    {data.sites
                      .sort((a, b) => b.visitors - a.visitors)
                      .map((s) => (
                        <div key={s.site} className="mb-2">
                          <div className="flex justify-between text-[12.5px]">
                            <span className="text-ink">{SITE_LABEL[s.site] ?? s.site}</span>
                            <span className="font-semibold text-ink">{num(s.visitors)}</span>
                          </div>
                          <ShareBar value={s.visitors} max={k.visitors} color="#5B7083" />
                        </div>
                      ))}
                  </CardBody>
                </Card>
              )}
            </div>
          </div>

          {sites.includes('rxk') && data.key_pages && <KeyPagesCard rows={data.key_pages} products={rxkProducts} />}

          {sites.includes('rxk') && data.staff_orders && (
            <Card>
              <CardHeader title="Orders taken by staff" subtitle={'Rx.K Seoul orders entered by hand, by the "Came from" choice (Facebook message, Instagram DM, call…)'} />
              <CardBody>
                <Table
                  head={['Came from', 'Orders', 'Sales']}
                  empty="No staff-entered orders in this period."
                  rows={data.staff_orders.map((r) => [
                    <span key="c" className="inline-flex items-center gap-2 font-semibold text-ink">
                      <Swatch source={r.source} /> {r.came_from === 'Not recorded' ? 'Not recorded (older orders)' : r.came_from}
                    </span>,
                    num(r.orders),
                    bdt(r.revenue),
                  ])}
                />
              </CardBody>
            </Card>
          )}

          <Card>
            <CardHeader title="Links & campaigns" subtitle="Visits that came through a tracked link or a tagged (UTM) URL" icon={<Link2 className="h-4 w-4" />} />
            <CardBody>
              <Table
                head={['Link / campaign', 'Visitors', 'Orders', 'Sales', 'Ordered']}
                empty="No tracked links or tagged URLs were used in this period."
                rows={data.campaigns.map((c) => [
                  <div key="c">
                    <span className="flex items-center gap-2 font-semibold text-ink">
                      {c.source && <Swatch source={c.source} />} {c.name ?? c.campaign}
                    </span>
                    {c.name && <span className="font-mono text-[10.5px] text-ink-faint">/go/{c.campaign}</span>}
                  </div>,
                  num(c.visitors),
                  num(c.orders),
                  bdt(c.revenue),
                  pct(c.orders, c.visitors),
                ])}
              />
            </CardBody>
          </Card>

          <div className="grid gap-5 lg:grid-cols-3">
            <Card>
              <CardHeader title="Top pages" />
              <CardBody>
                <Table head={['Page', 'Views', 'Visitors']} empty="—" rows={data.pages.slice(0, 15).map((p) => [<span key="p" className="break-all text-ink">{siteTag(p.site_key)}{p.path}</span>, num(p.views), num(p.visitors)])} />
              </CardBody>
            </Card>
            <Card>
              <CardHeader title="Products viewed" />
              <CardBody>
                <Table
                  head={['Product', 'Views', 'Top source']}
                  empty="No product pages viewed."
                  rows={data.products.slice(0, 15).map((p) => [
                    <span key="p" className="text-ink">
                      {siteTag(p.site_key)}
                      {names.get(p.product) ?? p.product}
                    </span>,
                    num(p.views),
                    p.top_source ? (
                      <span key="t" className="inline-flex items-center gap-1.5">
                        <Swatch source={p.top_source} /> {p.top_source}
                      </span>
                    ) : (
                      '—'
                    ),
                  ])}
                />
              </CardBody>
            </Card>
            <Card>
              <CardHeader title="First page visitors see" />
              <CardBody>
                <Table
                  head={['Landing page', 'Visitors', 'Top source']}
                  empty="—"
                  rows={data.landing.slice(0, 15).map((p) => [
                    <span key="l" className="break-all text-ink">
                      {siteTag(p.site_key)}
                      {p.path}
                    </span>,
                    num(p.visitors),
                    p.top_source ? (
                      <span key="t" className="inline-flex items-center gap-1.5">
                        <Swatch source={p.top_source} /> {p.top_source}
                      </span>
                    ) : (
                      '—'
                    ),
                  ])}
                />
              </CardBody>
            </Card>
          </div>

          <LinksPanel sites={allowed} stats={data.campaigns} canManage={manageLinks} onChanged={load} />

          <Card>
            <CardHeader title="Latest visitors" subtitle="Where each new visit started (most recent first)" />
            <CardBody>
              <Table
                head={['When', 'Page', 'Source', 'Link / campaign', 'Device']}
                empty="No visits yet."
                rows={data.recent.map((r) => [
                  <span key="w" className="whitespace-nowrap text-ink-faint">
                    {new Date(r.at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dhaka' })}
                  </span>,
                  <span key="p" className="break-all text-ink">
                    {siteTag(r.site_key)}
                    {r.path}
                  </span>,
                  <span key="s" className="inline-flex items-center gap-1.5">
                    <Swatch source={r.source} /> {r.source}
                  </span>,
                  r.campaign ?? '—',
                  r.device ?? '—',
                ])}
              />
            </CardBody>
          </Card>
          <p className="text-[11px] text-ink-faint">
            Visitors are counted per browser (no personal data). Instagram and Facebook app visits are recognised even when the app hides where they came from. Staff pages and
            test visits are left out. Orders count web orders (Rx.K Seoul checkout and Pick.K Seoul seller links); the source of an order is the visitor's last known source within 30
            days.
          </p>
          <div className="rounded-xl border border-border-subtle bg-white px-4 py-3 text-[12px] text-ink-faint">
            <p className="font-semibold text-ink">Don't count your own visits</p>
            <p className="mt-0.5">
              Staff visits are left out when you are logged in, when you've opened the Rx.K Seoul or Pick.K Seoul admin on that browser, or when you're on an office
              connection. For any other phone or browser (e.g. Safari with iCloud Private Relay, mobile data), open this once on it:
            </p>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
              {allowed.map((s) => (
                <a key={s} href={`${SITE_URL[s]}/?staff=1`} target="_blank" rel="noreferrer" className="font-mono text-[11.5px] text-primary-700 hover:underline">
                  {SITE_URL[s].replace('https://', '')}/?staff=1
                </a>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
